import { ref } from "vue";
import { hash } from "ohash";
import { getApiKey } from "../providers/api-key.provider";
import { ErrorOr, isError } from "./types";

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "OPTIONS";

export const plainQueryBuilder = (input: unknown) => input;

interface CreateUseApiParameters<TInputData> {
  requireAuthentication?: boolean;
  method: Method;
  baseURL?: string;
  // true by default
  attachXLocaleHeader?: boolean;
  url: string | ((inputData: TInputData) => string);
  dataBuilder?: (inputData: TInputData) => unknown;
  queryBuilder?: (inputData: TInputData) => unknown;
}

type DataOrEmptyIfVoid<T> = T extends void ? {} : { data: T };

type UseApiImmediateParameters<T> =
  | ({
      immediate: true;
    } & DataOrEmptyIfVoid<T>)
  | { immediate: false };

type UseApiParameters<T> = {
  server?: boolean;
  baseUrl?: string;
} & UseApiImmediateParameters<T>;

// const resultObject = {
//     errorMessage,
//     loading,
//     result: result as TResult,
//     fetch,
//     status: statusCode,
//     loadedOnce,
// };

type UseApiResult<TInput, TResult> = {
  result: Ref<TResult>;
  errorMessage: Ref<string | undefined>;
  loading: Ref<boolean>;
  fetch: (input: TInput) => Promise<TResult>;
  status: Ref<number | undefined>;
  loadedOnce: Ref<boolean>;
};

export const createUseApi =
  <TInputData, TResult>(opts: CreateUseApiParameters<TInputData>) =>
  (
    params?: UseApiParameters<TInputData>,
  ): UseApiResult<TInputData, TResult> &
    Promise<UseApiResult<TInputData, TResult>> => {
    const statusCode = ref<number>();
    const controller = new AbortController();
    const loadedOnce = ref<boolean>(false);
    const errorMessage = ref<string>();

    const config = useRuntimeConfig();

    let fetchInput: TInputData | undefined =
      params?.immediate && "data" in params ? params.data : undefined;

    const _key = hash([
      unref(opts.baseURL),
      unref(opts.url),
      unref(fetchInput),
    ]);

    const attachXLocale = opts.attachXLocaleHeader ?? true;

    const { locale } = attachXLocale ? useI18n() : { locale: { value: "en" } };

    const server = params?.server ?? true;
    const serverImmediate = !!(params?.immediate && server);

    const asyncData = useAsyncData<TResult>(
      _key,
      async () => {
        const url =
          typeof opts.url === "function"
            ? opts.url(fetchInput as TInputData)
            : opts.url;

        const body = opts.dataBuilder
          ? opts.dataBuilder(fetchInput as TInputData)
          : undefined;

        const query = opts.queryBuilder
          ? opts.queryBuilder(fetchInput as TInputData)
          : undefined;

        const headers = new Headers();

        if (opts.requireAuthentication)
          headers.set("Authorization", `Bearer ${getApiKey()}`);
        if (attachXLocale) headers.set("X-Locale", locale.value);

        try {
          const res = await $fetch<ErrorOr<TResult>>(url, {
            baseURL:
              opts?.baseURL ?? params?.baseUrl ?? config.public.apiBaseUrl,
            method: opts.method,
            // @TODO error?
            body: body as Record<string, any>,
            query: query as any,
            signal: controller.signal,
            headers,
            onResponse: ({ response }) => {
              statusCode.value = response?.status;
            },
          });

          if (isError(res)) {
            errorMessage.value = res.message;
            throw res;
          }

          return res;
        } catch (err) {
          if (
            err &&
            typeof err === "object" &&
            "message" in err &&
            typeof err.message === "string"
          ) {
            errorMessage.value = err.message;
            throw err;
          }

          errorMessage.value = JSON.stringify(err);
          throw err;
        }
      },
      {
        // immediate: true,
        // server: true,
        immediate: serverImmediate,
        server: serverImmediate,
      },
    );

    const { data: result, execute, pending: loading, error } = asyncData;

    const stopLoadingWatch = watch(loading, (loadingUpdate) => {
      if (!loadingUpdate) {
        loadedOnce.value = true;
        stopLoadingWatch();
      }
    });

    const nuxtApp = useNuxtApp();

    const fetch = async (inputData: TInputData) => {
      fetchInput = inputData;
      await execute();
      if (error.value) {
        throw error.value;
      }
      return result.value as TResult;
    };

    if (params?.immediate && !serverImmediate && !nuxtApp.ssrContext) {
      execute({
        _initial: false,
        dedupe: true,
      });
      // fetch(("data" in params ? params.data : undefined) as TInputData);
    }

    const resultObject = {
      errorMessage,
      loading,
      result: result as Ref<TResult>,
      fetch,
      status: statusCode,
      loadedOnce,
    };

    const resultPromise = (async () => {
      const { data: resultAsync, pending: loadingAsync } = await asyncData;

      return {
        errorMessage,
        loading: loadingAsync,
        result: resultAsync as Ref<TResult>,
        fetch,
        status: statusCode,
        loadedOnce,
      };
    })();

    return Object.assign(resultPromise, resultObject);
  };
